/**
 * @fileoverview `<LanguageSwitcher>` — sélecteur de langue UI-only.
 *
 * Le composant centralise le bouton globe + menu radio utilisé par le portail.
 * Il ne traduit rien et ne persiste rien : l'app appelante contrôle `value`,
 * `onValueChange` et le stockage éventuel.
 */

import { Globe2 } from 'lucide-react';
import { cn } from '../lib/utils.ts';
import { Button } from './ui/button.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu.tsx';

/**
 * Langue affichable dans le sélecteur.
 */
export interface LanguageSwitcherOption<TValue extends string = string> {
  /** Code stable utilisé par l'app appelante, par exemple `fr`, `en`, `es`, `de`. */
  code: TValue;
  /** Libellé court affiché dans le bouton, par exemple `FR`. */
  shortLabel: string;
  /** Nom natif affiché dans le menu, par exemple `Français`. */
  nativeName: string;
}

/**
 * Props publiques de `LanguageSwitcher`.
 */
export interface LanguageSwitcherProps<TValue extends string = string> {
  /** Code de langue actuellement sélectionné. */
  value: TValue;
  /** Liste des langues disponibles. Fournir au moins une option. */
  options: readonly LanguageSwitcherOption<TValue>[];
  /** Callback appelé quand l'utilisateur sélectionne une langue. */
  onValueChange: (value: TValue) => void;
  /** Surface visuelle du bouton : `dark` sur header navy, `light` sur fond clair. */
  surface?: 'dark' | 'light';
  /** Alignement Radix du menu déroulant. */
  align?: 'start' | 'center' | 'end';
  /** Label accessible et titre du menu. */
  label?: string;
  /** Affiche le code court à côté du globe. Défaut : `true`. */
  showShortLabel?: boolean;
  /** Classe CSS complémentaire appliquée au bouton trigger. */
  className?: string;
}

const SURFACE_CLASSES = {
  dark: 'text-white/80 hover:bg-white/10 hover:text-white',
  light: 'text-quipux-navy-900 hover:bg-quipux-navy-50 hover:text-quipux-navy-950',
} as const;

/**
 * Sélecteur de langue réutilisable.
 *
 * @param props - Valeur contrôlée, options, callback, surface, alignement, label et classe.
 * @returns Bouton globe avec menu radio accessible.
 *
 * @example
 * ```tsx
 * <LanguageSwitcher
 *   value="fr"
 *   options={[
 *     { code: 'fr', shortLabel: 'FR', nativeName: 'Français' },
 *     { code: 'en', shortLabel: 'EN', nativeName: 'English' },
 *   ]}
 *   onValueChange={setLanguage}
 *   surface="dark"
 * />
 * ```
 */
export function LanguageSwitcher<TValue extends string = string>({
  value,
  options,
  onValueChange,
  surface = 'light',
  align = 'end',
  label = 'Langue',
  showShortLabel = true,
  className,
}: LanguageSwitcherProps<TValue>): React.JSX.Element {
  const currentOption = options.find((option) => option.code === value) ?? options[0];
  const currentLabel = currentOption?.shortLabel ?? '—';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('h-10 px-2', SURFACE_CLASSES[surface], className)}
          aria-label="Changer la langue"
        >
          <Globe2 className="size-4" />
          {showShortLabel ? (
            <span className="font-mono text-xs font-semibold">{currentLabel}</span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-48">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(nextValue) => onValueChange(nextValue as TValue)}
        >
          {options.map((option) => (
            <DropdownMenuRadioItem key={option.code} value={option.code}>
              <span className="font-mono text-xs font-semibold">{option.shortLabel}</span>
              <span>{option.nativeName}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
